/**
 * EventDateTime component for displaying event date and time information
 * Handles both single-day and multi-day events with improved UX
 */

import { Calendar, ChevronRight, Clock } from 'lucide-react';

interface EventDateTimeProps {
  /**
   * Start timestamp (Unix timestamp in seconds)
   */
  startTimestamp: number;
  /**
   * End timestamp (Unix timestamp in seconds), optional
   */
  endTimestamp?: number;
  /**
   * Start date object
   */
  startDate: Date | null;
  /**
   * End date object
   */
  endDate: Date | null;
  /**
   * Whether start and end dates are on the same day
   */
  isSameDay: boolean;
  /**
   * Size variant
   */
  size?: 'default' | 'sm';
}

/**
 * Formats a date timestamp to a full date string
 */
function formatDate(timestamp: number, size: 'default' | 'sm' = 'default'): string {
  const date = new Date(timestamp * 1000);
  if (size === 'sm') {
    // Shorter format for small size (like event cards)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Formats a timestamp to a time string
 */
function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Checks if a timestamp is at midnight (00:00)
 */
function isMidnight(timestamp: number): boolean {
  const date = new Date(timestamp * 1000);
  return date.getHours() === 0 && date.getMinutes() === 0;
}

/**
 * Calculates the number of days between two dates (inclusive)
 * Returns the total number of days including both start and end dates
 */
function calculateDaysDifference(startDate: Date, endDate: Date): number {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  // Add 1 to include both start and end dates (inclusive)
  return diffDays + 1;
}

/**
 * EventDateTime component displays event date and time information
 * with improved UX for multi-day events
 */
export function EventDateTime({
  startTimestamp,
  endTimestamp,
  startDate,
  endDate,
  isSameDay,
  size = 'default',
}: EventDateTimeProps) {
  const daysDifference =
    startDate && endDate && !isSameDay ? calculateDaysDifference(startDate, endDate) : null;

  // Check if times are at midnight (00:00) - don't show time in that case
  const showStartTime = !isMidnight(startTimestamp);
  const showEndTime = endTimestamp ? !isMidnight(endTimestamp) : false;

  const isSmall = size === 'sm';
  const iconSize = isSmall ? 'h-3.5 w-3.5 mt-0.5' : 'h-5 w-5';
  const iconContainerSize = isSmall ? 'h-6 w-6' : 'h-10 w-10';
  const textSize = isSmall ? 'text-sm' : 'text-base';
  const dateTextSize = isSmall ? 'text-xs' : 'text-sm';
  const gapSize = isSmall ? 'gap-2' : 'gap-3';
  const arrowContainerSize = isSmall ? 'h-6 w-6' : 'h-10 w-10';
  const arrowIconSize = isSmall ? 'h-3.5 w-3.5' : 'h-5 w-5';

  return (
    <div className={`flex items-start ${gapSize}`}>
      {isSmall ? (
        /* Small size: icon directly in flex container like event cards */
        <Calendar className={`${iconSize} shrink-0 text-muted-foreground`} />
      ) : (
        /* Default size: icon in decorated container */
        <div
          className={`mt-0.5 flex ${iconContainerSize} shrink-0 items-center justify-center rounded-lg bg-primary/10`}
        >
          <Calendar className={`${iconSize} text-primary`} />
        </div>
      )}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Date and Time - Single day or multi-day */}
        {endTimestamp && !isSameDay && startDate && endDate ? (
          /* Multi-day event: dates on same line with arrow and badge */
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Start Date */}
              <div className="min-w-0">
                <div className={`font-semibold ${textSize}`} suppressHydrationWarning>
                  {formatDate(startTimestamp, size)}
                </div>
                {showStartTime ? (
                  <div
                    className={`mt-1 flex items-center gap-2 ${dateTextSize} text-muted-foreground`}
                  >
                    {!isSmall && <Clock className={`${iconSize} shrink-0 text-muted-foreground`} />}
                    <span suppressHydrationWarning>{formatTime(startTimestamp)}</span>
                  </div>
                ) : (
                  <div className="mt-1 h-4" />
                )}
              </div>

              {/* Arrow with days badge - Modern UX */}
              <div className="flex flex-col items-center px-3">
                {isSmall ? (
                  /* Small size: simple arrow icon */
                  <ChevronRight className={`${arrowIconSize} text-muted-foreground`} />
                ) : (
                  /* Default size: arrow in decorated container */
                  <div
                    className={`mt-0.5 flex ${arrowContainerSize} shrink-0 items-center justify-center rounded-lg bg-primary/5`}
                  >
                    <ChevronRight className={`${arrowIconSize} text-primary`} />
                  </div>
                )}
                {daysDifference !== null && (
                  <span className="text-[10px] font-normal opacity-80">
                    {daysDifference} {daysDifference === 1 ? 'day' : 'days'}
                  </span>
                )}
              </div>

              {/* End Date */}
              <div className="flex-1 min-w-0">
                <div className={`font-semibold ${textSize}`} suppressHydrationWarning>
                  {formatDate(endTimestamp, size)}
                </div>
                {showEndTime ? (
                  <div
                    className={`mt-1 flex items-center gap-2 ${dateTextSize} text-muted-foreground`}
                  >
                    {!isSmall && <Clock className={`${iconSize} shrink-0 text-muted-foreground`} />}
                    <span suppressHydrationWarning>{formatTime(endTimestamp)}</span>
                  </div>
                ) : (
                  <div className="mt-1 h-4" />
                )}
              </div>
            </div>
          </>
        ) : (
          /* Single day event */
          <div>
            <div className={`font-semibold ${textSize}`} suppressHydrationWarning>
              {formatDate(startTimestamp, size)}
            </div>
            {startDate && (showStartTime || showEndTime) && (
              <div className={`mt-1 flex items-center gap-2 ${dateTextSize} text-muted-foreground`}>
                {!isSmall && <Clock className={`${iconSize} shrink-0 text-muted-foreground`} />}
                <span suppressHydrationWarning>
                  {showStartTime && formatTime(startTimestamp)}
                  {showStartTime && showEndTime && endTimestamp && ' - '}
                  {showEndTime && endTimestamp && formatTime(endTimestamp)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
